<template>
  <div>
    <v-container fluid class="vacancyDetail">
      <v-container>
        <h1 class="d-lg-none">{{ vacancy.title }}</h1>
        <v-row class="pa-0" justify="space-between">
          <v-col cols="12" md="7" xl="7" xxl="7">
            <h1 class="d-none d-lg-block">{{ vacancy.title }}</h1>
            <div class="d-flex align-center mb-4 mt-4">
              <v-icon class="mr-2">mdi-map-marker</v-icon>
              <span class="mr-8  text-sm-body-1">{{ [vacancy.location?.city].filter(Boolean).join(', ') }}</span>
            </div>
            <div>
              <div v-if="vacancy.description" v-html="descriptionWithoutTitle"></div>
            </div>

            <div v-if="vacancyAddress && googleMapsApiKey" class="mb-8">
              <h2>{{ $t('vacancies.detail.location', 'Locatie') }}</h2>
              <p class="pt-2">
                <VacancyLocation :address="vacancyAddress" />
              </p>
            </div>

            <div id="application-form" class="mb-8 d-xl-none">
              <h2>{{ $t('vacancies.detail.apply', 'Solliciteer') }}</h2>
              <p class="pt-2">
                <VacancyApplicationForm :vacancy="vacancy" @submitted="onApplicationSubmitted" @error="onApplicationError" />
              </p>
            </div>
          </v-col>

          <v-col cols="12" md="5" xl="3" xxl="5" class="pt-5">
            <v-row>
              <v-col cols="12" xxl="6">
                <v-card class="vacancyDetailCard h-100">
                  <v-card-title>{{ $t('vacancies.detail.vacancyDetails', 'Vacaturedetails') }}</v-card-title>
                  <v-card-text>
                    <v-sheet class="d-flex align-end flex-column w-100 pa-0 h-100">
                          <v-sheet class="pa-0 w-100 h-100 mb-3">


                                    <div >
                                      <v-icon>mdi-map-marker</v-icon>
                                      {{ vacancy.location?.city }}
                                    </div>
                                    <div>
                                      <v-icon>mdi-clock-time-five-outline</v-icon>
                                      {{ vacancy.workingHours?.from === vacancy.workingHours?.to ? vacancy.workingHours?.to : `${vacancy.workingHours?.from}-${vacancy.workingHours?.to}` }} {{ $t('vacancies.detail.hoursPerWeek', 'uur/week') }}
                                    </div>
                                    <div v-if="vacancy.salary?.from && vacancy.salary?.to"
                                    class=" d-flex">
                                      <v-icon>mdi-currency-eur</v-icon>
                                      €{{ vacancy.salary.from }} - €{{ vacancy.salary.to }}
                                    </div>
                                    <div class=" d-flex">
                                      <v-icon>mdi-calendar-clock-outline</v-icon>
                                      {{ $t('vacancies.detail.startDate', 'Startdatum') }}: {{ new Date(vacancy.startDate) < new Date() ? $t('vacancies.detail.asap', 'Zo snel mogelijk') : vacancy.startDate }}
                                    </div>
                                    <div class=" d-flex">
                                      <v-icon>mdi-calendar-check-outline</v-icon>
                                      {{ $t('vacancies.detail.endDate', 'Einddatum') }}: {{ vacancy.employmentType === 'permanent' ? $t('vacancies.detail.indefinite', 'onbepaald') : $t('vacancies.detail.temporary', 'Tijdelijk') }}
                                    </div>
                          </v-sheet>
                          <v-sheet class="mt-auto w-100">
                                    <v-btn color="primary mt-auto" block @click="scrollToForm" class="mt-3 text-none">
                                      {{ $t('vacancies.detail.applyNow', 'Solliciteer nu') }}
                                    </v-btn>
                          </v-sheet>
                      </v-sheet>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" xxl="6">
                <ContactCard
                  :chat-options="getChatOptions()"
                  :phone-numbers="getPhoneNumbers()"
                  :email-addresses="getEmailAddresses()"
                  :visit-info="vacancyAddress ? {
                    address: vacancyAddress
                  } : null"
                />
                </v-col>
            </v-row>

            <div id="application-form" class="mb-8 d-none d-xl-block">
              <h2 class="pt-10">{{ $t('vacancies.detail.apply', 'Solliciteer') }}</h2>
              <p class="pt-2">
                <VacancyApplicationForm :vacancy="vacancy" @submitted="onApplicationSubmitted" @error="onApplicationError" />
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script setup>
import ContactCard from './ContactCard.vue'
import VacancyLocation from './VacancyLocation.vue'
import VacancyApplicationForm from './VacancyApplicationForm.vue'

const { t } = useI18n()
const { GOOGLE_MAPS_API_KEY: googleMapsApiKey } = useRuntimeConfig().public
const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

const props = defineProps({
  vacancy: {
    type: Object,
    default: () => ({})
  }
})

const scrollToForm = () => {
  document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const onApplicationSubmitted = () => {
  snackbar.value = { show: true, message: t('vacancies.form.success', 'Sollicitatie succesvol verzonden!'), color: 'success' }
}

const onApplicationError = () => {
  snackbar.value = { show: true, message: t('vacancies.form.error', 'Er is iets misgegaan. Probeer het opnieuw.'), color: 'error' }
}

const getDaysSinceModifiedText = (modified) => {
  if (!modified) return ''
  const modifiedDate = new Date(modified)
  const currentDate = new Date()
  const diffTime = Math.abs(currentDate - modifiedDate)
  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return diffTime > 5 * 24 * 60 * 60 * 1000 ? t('vacancies.detail.daysAgo', { days }, `${days} dagen geleden`) : t('vacancies.detail.new', 'Nieuw')
}

const descriptionWithoutTitle = computed(() => {
  if (!props.vacancy.description) return ''
  return props.vacancy.description.replace(/<h1[^>]*>.*?<\/h1>/i, '')
})

const vacancyAddress = computed(() => {
  const loc = props.vacancy.location
  if (!loc) return null
  return [loc.addressLine1, loc.postcode, loc.city].filter(Boolean).join(', ') || null
})

const chipColor = computed(() => {
  const text = getDaysSinceModifiedText(props.vacancy.updatedAt)
  return text === t('vacancies.detail.new', 'Nieuw') ? 'success' : 'light-blue'
})

const getChatOptions = () => {
  const numbers = new Set()
  if (props.vacancy.user?.phone) {
    numbers.add(props.vacancy.user.phone)
  }
  return Array.from(numbers).map(number => ({
    label: 'WhatsApp',
    icon: 'mdi-whatsapp',
    url: `https://api.whatsapp.com/send?phone=${number.replace(/\D/g, '')}`
  }))
}

const getPhoneNumbers = () => {
  const numbers = new Set()
  if (props.vacancy.user?.phone) {
    numbers.add(props.vacancy.user.phone)
  }
  return Array.from(numbers).map(number => ({
    number,
    display: number
  }))
}

const getEmailAddresses = () => {
  const emails = new Set()
  if (props.vacancy.user?.email) {
    emails.add(props.vacancy.user.email)
  }
  return Array.from(emails).map(address => ({ address }))
}
</script>
